import { ReorderProjectDocument, type ReorderProjectMutation } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import type { ProjectFields } from "@/types/Project";
import { useMutation } from "@apollo/client/react";

export function useReorderProject() {
  const [reorderProjectMutation, { loading }] = useMutation(ReorderProjectDocument);

  const reorderProject = (
    project: ProjectFields,
    parentId: string | null,
    prevSortOrder: number | null,
    newSortOrder: number | null
  ) => {
    return withToast(
      async () => {
        const optimisticSortOrder = newSortOrder ?? (prevSortOrder ?? 0) + 1000;

        return reorderProjectMutation({
          variables: {
            projectId: project.id,
            parentId,
            prevSortOrder,
            newSortOrder,
          },
          // The server may renumber siblings too, but only the moved project can be
          // predicted here; the real response replaces this layer with the full set.
          optimisticResponse: {
            reorderProject: [
              {
                __typename: "Project",
                ...project,
                sortOrder: optimisticSortOrder,
              } as ReorderProjectMutation["reorderProject"][number],
            ],
          },
        });
      },
      {
        loading: "Updating...",
        success: "Project Order Updated!",
        error: "Failed to update project order. Please try again.",
      },
      "reorderProjectToast"
    );
  };

  return { reorderProject, loading };
}
